import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  List,
  Stack,
  Typography,
} from '@mui/material';
import { Cancel as RemoveIcon, Check as CheckIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ColoredUserAvatar from '../../../components/ColoredUserAvatar';
import DebouncedSearchInput from '../../../components/DebouncedSearchInput';
import FullScreenDialog from '../../../components/FullScreenDialog';
import { useLeadPickerQuery } from '../hooks/useLeadsQuery';
import { LeadListItem, LoadMoreSentinel, leadName } from './Leads.ListLeadItems';

// "Adicionar à lista" (como no WhatsApp): busca + leads paginados com
// checkbox. Os escolhidos nesta tela aparecem no topo, com um X para desfazer.
// `initialSelected`: uuids já na lista. `onConfirm(uuids, leadsByUuid)` recebe
// a seleção final e os leads carregados aqui (para quem precisa exibir nomes).
const LeadsLeadPickerDialog = ({ open, initialSelected, onClose, onConfirm, isSaving = false }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set(initialSelected));
  const [picked, setPicked] = useState([]); // leads marcados nesta tela, na ordem

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useLeadPickerQuery({ search });
  const leads = useMemo(() => data?.pages.flatMap((page) => page.results) ?? [], [data]);

  const toggle = (lead) => {
    const isSelected = selected.has(lead.uuid);
    setSelected((prev) => {
      const next = new Set(prev);
      if (isSelected) next.delete(lead.uuid);
      else next.add(lead.uuid);
      return next;
    });
    setPicked((prev) => (isSelected
      ? prev.filter((item) => item.uuid !== lead.uuid)
      : [...prev.filter((item) => item.uuid !== lead.uuid), lead]));
  };

  const handleConfirm = () => {
    const known = Object.fromEntries([...leads, ...picked].map((lead) => [lead.uuid, lead]));
    onConfirm([...selected], known);
  };

  return (
    <FullScreenDialog
      open={open}
      onClose={onClose}
      title={t('leads.lists.addToListTitle')}
      maxWidth="sm"
      actions={(
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={<CheckIcon />}
          disabled={isSaving}
          onClick={handleConfirm}
        >
          {t('leads.lists.done')}
        </Button>
      )}
    >
      <Stack spacing={2}>
        <DebouncedSearchInput onSearch={setSearch} placeholder={t('leads.lists.searchConversations')} />
        {picked.length > 0 && (
          <>
            <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', pb: 0.5 }}>
              {picked.map((lead) => (
                <Stack key={lead.uuid} alignItems="center" spacing={0.5} sx={{ width: 64, flexShrink: 0 }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={(
                      <IconButton
                        size="small"
                        aria-label={t('leads.lists.removeLead', { name: leadName(lead) })}
                        onClick={() => toggle(lead)}
                        sx={{ p: 0, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}
                      >
                        <RemoveIcon fontSize="small" color="action" />
                      </IconButton>
                    )}
                  >
                    <ColoredUserAvatar name={leadName(lead)} sx={{ width: 48, height: 48 }} />
                  </Badge>
                  <Typography variant="caption" noWrap sx={{ maxWidth: '100%' }}>{leadName(lead)}</Typography>
                </Stack>
              ))}
            </Stack>
            <Divider />
          </>
        )}
      </Stack>

      {isLoading ? (
        <Stack alignItems="center" py={3}>
          <CircularProgress size={24} />
        </Stack>
      ) : (
        <List dense>
          {leads.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {t('leads.lists.noResults')}
            </Typography>
          )}
          {leads.map((lead) => (
            <LeadListItem
              key={lead.uuid}
              lead={lead}
              onClick={() => toggle(lead)}
              action={(
                <Checkbox
                  edge="end"
                  checked={selected.has(lead.uuid)}
                  onChange={() => toggle(lead)}
                  slotProps={{ input: { 'aria-label': leadName(lead) } }}
                />
              )}
            />
          ))}
          <LoadMoreSentinel
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        </List>
      )}
    </FullScreenDialog>
  );
};

export default LeadsLeadPickerDialog;
